import { useEffect, useMemo, useState } from "react";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useIntl } from "react-intl";
import { Box, Typography } from '@mui/material';
import { CustomSelect } from "../../common";
import { createColumns } from "../helpers/kanbanUtils";
import KanbanColumn from "./KanbanColumn";

const Kanban = ({
  enableEdit,
  onTaskAction,
  project,
  selectedFieldId,
  tasks,
  userLookup,
  //viewColumnIds,
}) => {
  const intl = useIntl();

  const [selectedStatusFieldId, setSelectedStatusFieldId] = useState("");
  const [selectOptions, setSelectOptions] = useState([]);
  const [statusFields, setStatusFields] = useState();
  const [taskDetailsColumns, setTaskDetailsColumns] = useState([]);

  const handleStatusFieldChange = (event) => setSelectedStatusFieldId(event.target.value);

  useEffect(() => {
    if (project) {
      const fields = project.projectFields.filter(field => field.type === 3) || [];
      if (fields.length === 0) {
        return;
      }

      const selectedField = fields.find(field => field.fieldId === selectedFieldId);

      setSelectedStatusFieldId(
        (selectedField || fields[0]).fieldId
      );

      setSelectOptions(
        (selectedField ? [selectedField] : fields)
          .map(
            field => ({
              value: field.fieldId,
              label: field.name,
            }))
      );

      setStatusFields(fields);
    }
  }, [project, selectedFieldId]);

  useEffect(() => {
    if (project && Array.isArray(tasks)) {
      var columnsList = createColumns(project.projectFields);
      setTaskDetailsColumns(columnsList);
    }
  }, [project, tasks]);

  const columns = useMemo(() => {
    if (!project || !Array.isArray(tasks) || !statusFields || !selectedStatusFieldId) return [];

    const statusField = statusFields.find(field => field.fieldId === selectedStatusFieldId);
    if (!statusField) return [];

    let newColumns = [];
    try {
      const settings = JSON.parse(statusField.jsonFieldSettings);
      newColumns = settings.map(status => ({
        name: status.name,
        label: status.name,
        color: status.color,
        fieldId: statusField.fieldId,
        tasks: []
      }));
    } catch (e) {
      console.error("Error parsing JSON:", e);
    }

    newColumns.unshift({
      name: "PROJECT_KANBAN_UNDEFINED",
      label: intl.formatMessage({ id: "project.kanban.undefinedStatus" }),
      color: "#e0e0e0",
      fieldId: null,
      tasks: []
    });

    tasks.forEach(task => {
      let placed = false;
      task.values.forEach(value => {
        newColumns.forEach(column => {
          if (column.fieldId && value.fieldId === column.fieldId && value.stringValue === column.name) {
            column.tasks.push(task);
            placed = true;
          }
        });
      });
      if (!placed) {
        newColumns.find(column => column.name === "PROJECT_KANBAN_UNDEFINED").tasks.push(task);
      }
    });

    // Remove "PROJECT_KANBAN_UNDEFINED" if it is empty
    newColumns = newColumns.filter(column => column.name !== "PROJECT_KANBAN_UNDEFINED" || column.tasks.length > 0);

    return newColumns;
  }, [project, tasks, intl, selectedStatusFieldId, statusFields]);

  const haveSingleOption = (selectOptions.length === 1);
  const haveStatusFields = (statusFields?.length > 0);

  if (taskDetailsColumns.length === 0) {
    return;
  };

  return (
    <Box sx={{ overflowX: "auto", paddingTop: "4px", maxWidth: "calc(100vw - 340px)" }}>
      {haveStatusFields ? (
        <>
          {haveSingleOption ? (
            <Typography>{selectOptions[0].label}</Typography>
          ) : (
            <CustomSelect
              customClass=""
              fullWidth={false}
              inputLabel={intl.formatMessage({ id: "project.kanban.selectStatusField" })}
              onChange={handleStatusFieldChange}
              options={selectOptions}
              required
              shrink
              value={selectedStatusFieldId}
            />
          )}
          <Box display="flex" flexDirection="row" justifyContent="flex-start" className="kanban">
            <DndProvider backend={HTML5Backend}>
              {columns.map(column => (
                <KanbanColumn
                  column={column}
                  enableEdit={enableEdit}
                  key={column.name}
                  onTaskAction={onTaskAction}
                  taskDetailsColumns={taskDetailsColumns}
                  userLookup={userLookup}
                />
              ))}
            </DndProvider>
          </Box>
        </>
      ) : (
        <Typography>{intl.formatMessage({ id: "project.kanban.noStatusFields" })}</Typography>
      )}
    </Box>
  );
}

export default Kanban;
